import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, ArrowRight, RefreshCw, Lock } from 'lucide-react';

const WaitingScreen = ({ redirectUrl }) => {
  const [stage, setStage] = useState('sending');
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState('');
  const [redirect, setRedirect] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Email sending simulation
  useEffect(() => {
    if (stage === 'sending') {
      const timer = setTimeout(() => {
        setStage('sent');
      }, 3000);
      
      // Progress bar animation
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 3;
          return newProgress < 100 ? newProgress : 100;
        });
      }, 100);
      
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [stage]);

  // Animated dots for waiting message
  useEffect(() => {
    if (stage === 'sent') {
      const interval = setInterval(() => {
        setDots(prev => prev.length < 3 ? prev + '.' : '');
      }, 500);
      
      // Simulate server confirmation
      const serverTimer = setTimeout(() => {
        setStage('confirmed');
        setCountdown(5);
      }, 5000);
      
      return () => {
        clearInterval(interval);
        clearTimeout(serverTimer);
      };
    }
  }, [stage]);

  // Countdown and redirect
  useEffect(() => {
    if (countdown !== null) {
      if (countdown > 0) {
        const timer = setTimeout(() => {
          setCountdown(countdown - 1);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        setRedirect(true);
      }
    }
  }, [countdown]);

  // Particles for background animation
  const Particles = () => {
    const particles = Array.from({ length: 30 });
    
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((_, index) => {
          const size = Math.floor(Math.random() * 4) + 2;
          const posX = Math.floor(Math.random() * 100);
          const posY = Math.floor(Math.random() * 100);
          const duration = Math.floor(Math.random() * 20) + 10;
          const delay = Math.floor(Math.random() * 10);
          
          return (
            <div
              key={index}
              className="absolute bg-purple-500 rounded-full opacity-20"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                left: `${posX}%`,
                top: `${posY}%`,
                animation: `float ${duration}s infinite ease-in-out ${delay}s`,
              }}
            />
          );
        })}
      </div>
    );
  };

  // Digital circuit lines animation
  const CircuitLines = () => {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent" 
            style={{animation: "pulse 3s infinite ease-in-out"}} />
        <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-500 to-transparent"
            style={{animation: "pulse 3s infinite ease-in-out 1.5s"}} />
        <div className="absolute left-1/4 top-0 h-full w-px bg-gradient-to-b from-transparent via-purple-500 to-transparent"
            style={{animation: "pulse 3s infinite ease-in-out 0.5s"}} />
        <div className="absolute left-3/4 top-0 h-full w-px bg-gradient-to-b from-transparent via-indigo-500 to-transparent"
            style={{animation: "pulse 3s infinite ease-in-out 2s"}} />
      </div>
    );
  };

  // Email flying animation
  const EmailAnimation = () => {
    return (
      <div className="relative mb-8">
        <div className="w-20 h-20 mx-auto relative">
          {stage === 'sending' && (
            <div className="absolute inset-0 flex items-center justify-center animate-ping opacity-30">
              <Mail size={60} className="text-purple-500" />
            </div>
          )}
          <div className={`absolute inset-0 flex items-center justify-center ${stage === 'sending' ? 'animate-pulse' : stage === 'sent' ? 'animate-bounce' : ''}`}>
            <Mail size={60} className="text-purple-500" />
          </div>
          {stage === 'confirmed' && (
            <div className="absolute -right-2 -bottom-2 bg-green-500 rounded-full p-1">
              <CheckCircle size={24} className="text-gray-900" />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main rendering
  return (
    <div className="">
      {/* Background animations */}
      <Particles />
      <CircuitLines />
      
      {/* Hexagon grid pattern */}
      <div className="absolute inset-0 opacity-5" 
           style={{
             backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0l25.98 15v30L30 60 4.02 45V15L30 0z\' fill-rule=\'evenodd\' stroke=\'%23ffffff\' fill=\'none\'/%3E%3C/svg%3E")',
             backgroundSize: '60px 60px'
           }} />
      
      {/* Content container */}
      <div className="w-full max-w-md z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-mono font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-500">
            0x10LayerFF
          </h1>
          <p className="text-xs uppercase tracking-wider text-gray-400 mt-1">Security Protocol</p>
        </div>
        
        {/* Main card */}
        <div className="bg-gray-800 bg-opacity-80 backdrop-blur-lg rounded-xl shadow-2xl border border-gray-700 p-8 text-center">
          {/* Email animation */}
          <EmailAnimation />
          
          {/* Status title */}
          <h2 className="text-xl font-bold text-white mb-2">
            {stage === 'sending' ? 'Sending Reset Link' : 
             stage === 'sent' ? 'Reset Link Sent' : 
             'Password Reset'}
          </h2>
          
          {/* Status message */}
          <p className="text-gray-400 mb-6">
            {stage === 'sending' ? 'Preparing secure reset instructions...' : 
             stage === 'sent' ? `Waiting for confirmation${dots}` : 
             'if Your password has been reset.'}
          </p>
          
          {/* Progress indicator or countdown */}
          {stage === 'sending' && (
            <div className="w-full bg-gray-700 rounded-full h-2 mb-6 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          
          {stage === 'sent' && (
            <div className="flex items-center justify-center mb-6">
              <div className="animate-spin mr-3">
                <RefreshCw size={20} className="text-purple-500" />
              </div>
              <span className="text-sm text-gray-400">Awaiting confirmation</span>
            </div>
          )}
          
          {stage === 'confirmed' && (
            <>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-3 overflow-hidden">
                <div 
                  className="bg-green-500 h-full rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: `${(countdown / 5) * 100}%` }}
                />
              </div>
              <p className="text-sm text-gray-400 mb-6">
                Redirecting in <span className="font-mono text-green-500">{countdown}</span> seconds
              </p>
            </>
          )}
          
          {/* Action button */}
          {stage === 'sent' ? (
            <div className="flex items-center justify-center gap-3 text-sm text-gray-400 mb-2">
              <Lock size={14} />
              <span>Check your email for the reset link</span>
            </div>
          ) : stage === 'confirmed' ? (
            <a 
            href={redirectUrl || '/login'} 
              className={`inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg transition-all duration-300 ${redirect ? 'animate-pulse' : ''}`}
            >
              <span>Continue to Homepage</span>
              <ArrowRight size={18} />
            </a>
          ) : null}
        </div>
        
        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-8">
            © {new Date().getFullYear()} 0x10LayerFF • Secured Connection
        </p>
      </div>
      
      {/* CSS animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-15px); }
          75% { transform: translateY(-25px) translateX(5px); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default WaitingScreen;